<template>
    <header class="sticky top-0 z-50 w-full min-h-20 border-b border-gray-200 bg-white">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div class="flex h-20 min-h-20 shrink-0 items-center justify-between">
                <a :href="defPath" class="flex-shrink-0" aria-label="Home">
                    <img :src="logo" :alt="projectName" class="w-[120px] h-[50px] object-contain" />
                </a>
                <div class="flex items-center gap-3">
                    <button
                        type="button"
                        class="p-2 pr-0 rounded-lg text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-czech-blue focus:ring-offset-1 md:hidden"
                        aria-label="Open menu"
                        @click="isMenuOpen = true"
                    >
                        <svg class="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"/>
                        </svg>
                    </button>
                </div>
            </div>
        </div>

        <!-- Overlay -->
        <Transition name="fade">
            <div
                v-show="isMenuOpen"
                class="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm md:hidden"
                aria-hidden="true"
                @click="isMenuOpen = false"
            />
        </Transition>

        <!-- Slide-out panel from right -->
        <Transition name="slide-right">
            <aside
                v-show="isMenuOpen"
                class="fixed top-0 right-0 z-[70] h-full w-[min(320px,88vw)] bg-white shadow-2xl flex flex-col md:hidden"
                role="dialog"
                aria-modal="true"
                aria-label="Navigation menu"
            >
                <div class="flex items-center justify-between h-20 px-5 border-b border-gray-100">
                    <a :href="defPath" class="flex-shrink-0" @click="isMenuOpen = false" aria-label="Home">
                        <img :src="logo" :alt="projectName" class="h-8 w-auto max-w-[160px] object-contain" />
                    </a>
                    <button
                        type="button"
                        class="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-czech-blue"
                        aria-label="Close menu"
                        @click="isMenuOpen = false"
                    >
                        <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                        </svg>
                    </button>
                </div>
                <nav class="flex-1 overflow-y-auto py-6 px-4">
                    <ul class="list-none space-y-1 pl-0">
                        <li v-for="(item, index) in menuItems" :key="index">
                            <template v-if="item.children?.length">
                                <span class="block px-4 pb-1 rounded-xl text-xs font-semibold cursor-default"
                                      :class="itemChildrenActive(item) ? 'text-czech-blue' : 'text-gray-700'">
                                    {{ item.translated?.title || '' }}
                                </span>
                                <ul class="list-none ml-4 mt-1 space-y-1 border-l-2 border-gray-200 pl-4">
                                    <li v-for="(child, childIndex) in item.children" :key="childIndex">
                                        <a
                                            :href="menuHrefFor(child.path)"
                                            :target="isHelpCenterItem(child.path) ? '_blank' : undefined"
                                            :rel="menuItemRel(child.path)"
                                            class="block py-2.5 pr-4 rounded-lg text-xs font-medium transition-colors"
                                            :class="isItemActive(child.path) ? 'bg-czech-blue/10 text-czech-blue font-semibold' : 'text-gray-600 hover:bg-czech-blue/10 hover:text-czech-blue'"
                                            @click="isMenuOpen = false"
                                        >
                                            {{ child.translated?.title || '' }}
                                        </a>
                                    </li>
                                </ul>
                            </template>
                            <a
                                v-else
                                :href="menuHrefFor(item.path)"
                                :target="isHelpCenterItem(item.path) ? '_blank' : undefined"
                                :rel="isHelpCenterItem(item.path) ? 'noopener noreferrer nofollow' : undefined"
                                class="block px-4 py-1 rounded-xl text-xs font-semibold transition-colors"
                                :class="isItemActive(item.path) ? 'bg-czech-blue/10 text-czech-blue' : 'text-gray-700 hover:bg-czech-blue/10 hover:text-czech-blue'"
                                @click="isMenuOpen = false"
                            >
                                {{ item.translated?.title || '' }}
                            </a>
                        </li>
                    </ul>
                    <div class="mt-8 pt-6 border-t border-gray-100 flex flex-col gap-6">
                        <div class="w-full min-w-0">
                            <p class="px-1 mb-2 text-xs font-medium uppercase tracking-wider text-gray-700">
                                {{ texts['header-mobile.blade.php-menu-language-label'] ?? 'Language' }}
                            </p>
                            <div class="relative w-full min-w-0">
                                <button
                                    type="button"
                                    class="w-full inline-flex items-center gap-2.5 rounded-xl border border-gray-200 bg-gray-50/80 px-3.5 py-2.5 text-xs font-semibold text-gray-700 shadow-sm transition-all hover:border-gray-300 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-czech-blue/20 focus:ring-offset-1"
                                    @click="langOpen = !langOpen"
                                    :aria-expanded="langOpen"
                                >
                                    <img v-if="currentLang?.flag" :src="currentLang.flag" :alt="languageAlt(currentLang)" class="h-5 w-5 rounded-full object-cover ring-1 ring-gray-200/80 flex-shrink-0" />
                                    <span class="flex-1 text-left min-w-0 truncate">{{ languageLabel(currentLang) }}</span>
                                    <svg class="w-4 h-4 text-gray-500 flex-shrink-0 transition-transform" :class="{ 'rotate-180': langOpen }" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
                                    </svg>
                                </button>
                                <Transition name="dropdown">
                                    <div
                                        v-if="langOpen"
                                        class="absolute left-0 right-0 top-full z-10 mt-2 w-full max-h-[min(60vh,22rem)] overflow-y-auto py-2 bg-white rounded-lg shadow-lg ring-1 ring-black/5"
                                    >
                                        <div class="grid grid-cols-2 gap-x-2 gap-y-0.5 px-2">
                                            <a
                                                v-for="lang in availableLanguages"
                                                :key="lang.code"
                                                :href="pathsByLang[lang.code] || '#'"
                                                class="flex items-center gap-2.5 px-3 py-2 text-xs text-gray-700 transition-colors hover:bg-gray-50 rounded-lg min-w-0"
                                                :class="{ 'bg-czech-blue/5 text-czech-blue font-semibold': langCodesEqual(currentLangCode, lang.code) }"
                                                @click="isMenuOpen = false"
                                            >
                                                <img v-if="lang.flag" :src="lang.flag" :alt="languageAlt(lang)" class="h-5 w-5 rounded-full object-cover ring-1 ring-gray-200/80 flex-shrink-0" />
                                                <span class="truncate min-w-0">{{ languageLabel(lang) }}</span>
                                            </a>
                                        </div>
                                    </div>
                                </Transition>
                            </div>
                        </div>

                        <div v-if="showCurrencyConverter" class="cv-currency-select-wrap w-full min-w-0">
                            <p class="px-1 mb-2 text-xs font-medium uppercase tracking-wider text-gray-700">
                                {{ texts['header-mobile.blade.php-menu-currency-label'] ?? 'Currency' }}
                            </p>
                            <select-app-currency></select-app-currency>
                        </div>
                    </div>

                    <a
                        v-if="ctaText"
                        :href="ctaResolvedHref"
                        rel="nofollow"
                        class="mt-6 block w-full text-center bg-buy-green hover:bg-buy-green-hover text-white py-3.5 px-4 rounded-xl font-bold text-base shadow-lg transition-all"
                        @click="isMenuOpen = false"
                    >
                        {{ ctaText }}
                    </a>
                </nav>
            </aside>
        </Transition>
    </header>
</template>

<script>
import { mapGetters } from 'vuex';
import SelectAppCurrency from './inputs/SelectAppCurrency.vue';
import { isHelpCenterHref, resolveMenuHref } from '../shared/helpCenterResolver';
import { resolveBuyVignetteHref } from '../utils/buyVignetteFallbackUrl.js';

export default {
    name: 'MobileHeader',
    components: {
        SelectAppCurrency
    },
    props: {
        menuItems: { type: Array, default: () => [] },
        logo: { type: String, default: '' },
        projectName: { type: String, default: 'Logo' },
        pathsByLang: { type: Object, default: () => ({}) },
        ctaText: { type: String, default: '' },
        ctaHref: { type: String, default: '' },
        defPath: { type: String, default: '/' },
        currentLangCode: { type: String, default: '' },
        currentPath: { type: String, default: '' }
    },
    data() {
        return {
            isMenuOpen: false,
            langOpen: false
        };
    },
    computed: {
        ...mapGetters(['texts', 'showCurrencyConverter']),
        languagesList() {
            return this.$store.getters.languages || [];
        },
        currentLang() {
            const code = this.currentLangCode;
            if (!code) {
                return null;
            }

            const up = String(code).toUpperCase();

            return this.languagesList.find((l) => String(l.code || '').toUpperCase() === up) || null;
        },
        /** Լեզուներ, որոնք ցուցադրել (`paths_by_language` keys, մետադատա միայն API-ից) */
        availableLanguages() {
            const paths = this.pathsByLang || {};
            const list = this.languagesList;
            const byUpper = Object.fromEntries(
                list
                    .filter((l) => l && l.code !== undefined && l.code !== null && l.code !== '')
                    .map((l) => [String(l.code).toUpperCase(), l])
            );

            return Object.keys(paths)
                .map((code) => byUpper[String(code).toUpperCase()] || list.find((l) => l.code === code))
                .filter(Boolean);
        },
        /** Mobile CTA URL: Blade `ctaHref`, then meta, then English buy-vignette page. */
        ctaResolvedHref() {
            const fromProp = (this.ctaHref || '').trim();
            if (fromProp) {
                return fromProp;
            }
            if (typeof document !== 'undefined') {
                const el = document.querySelector('meta[name="czechvignette-buy-vignette-url"]');
                const raw = el?.getAttribute('content')?.trim();
                if (raw) {
                    try {
                        if (/^https?:\/\//i.test(raw)) {
                            return raw;
                        }
                        const path = raw.startsWith('/') ? raw : `/${raw}`;
                        return new URL(path, window.location.origin).href;
                    } catch {
                        /* fall through */
                    }
                }
            }
            return resolveBuyVignetteHref('');
        }
    },
    watch: {
        isMenuOpen(open) {
            document.body.style.overflow = open ? 'hidden' : '';
            if (!open) this.langOpen = false;
        }
    },
    methods: {
        langCodesEqual(a, b) {
            return String(a ?? '').toUpperCase() === String(b ?? '').toUpperCase();
        },
        pathFromHref(href) {
            if (!href || href === '#') return '';
            try {
                const path = typeof href === 'string' ? href : (href.path || '');
                const p = path.replace(/^https?:\/\/[^/]+/, '').replace(/#.*$/, '').trim().replace(/^\/+|\/+$/g, '');
                return p || '';
            } catch (_) { return ''; }
        },
        isItemActive(itemPath) {
            const current = (this.currentPath || '').replace(/^\/+|\/+$/g, '');
            const item = this.pathFromHref(itemPath);
            return item && (current === item || current.startsWith(item + '/'));
        },
        /**
         * Mirrors App\Support\HelpCenterUrl::menuHref() on the server side:
         * CMS menu items whose path matches a Help Center route (`/help-center`,
         * `helpcenter`, …) are rewritten to the localised Zendesk URL so the
         * hover preview and the actual click both bypass the storefront
         * `help-center.blade.php` redirector page.
         */
        menuHrefFor(itemPath) {
            return resolveMenuHref(itemPath, this.currentLangCode);
        },
        isHelpCenterItem(itemPath) {
            return isHelpCenterHref(itemPath);
        },
        isBuyVignetteItem(itemPath) {
            return this.pathFromHref(itemPath).includes('buy-vignette');
        },
        /** Help Center keeps target=_blank attrs; Purchase (buy-vignette) children get nofollow only. */
        menuItemRel(itemPath) {
            if (this.isHelpCenterItem(itemPath)) return 'noopener noreferrer nofollow';
            if (this.isBuyVignetteItem(itemPath)) return 'nofollow';
            return undefined;
        },
        itemChildrenActive(item) {
            return (item.children || []).some(c => this.isItemActive(c.path));
        },
        /** `translated.name` из CMS, затем code_view и code. */
        languageLabel(lang) {
            if (!lang) {
                return String(this.currentLangCode || '').toUpperCase() || '';
            }

            const raw = lang.translated?.name ?? lang.code_view ?? lang.code;

            return String(raw ?? '').trim() || String(lang.code || '').toUpperCase();
        },
        languageAlt(lang) {
            const base = this.languageLabel(lang) || 'Language';

            return `${base} flag`;
        }
    },
    beforeUnmount() {
        document.body.style.overflow = '';
    }
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.25s ease;
}
.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

.slide-right-enter-active,
.slide-right-leave-active {
    transition: transform 0.3s cubic-bezier(0.32, 0.72, 0, 1);
}
.slide-right-enter-from,
.slide-right-leave-to {
    transform: translateX(100%);
}

.dropdown-enter-active,
.dropdown-leave-active {
    transition: opacity 0.2s ease, transform 0.2s ease;
}
.dropdown-enter-from,
.dropdown-leave-to {
    opacity: 0;
    transform: translateY(-4px);
}
</style>
